import { getNatsClient, useKhalAuth } from '@khal-os/sdk/app';
import { useNotificationStore } from '@khal-os/ui';
import { useCallback } from 'react';
import { normalizeLaunchUrl, normalizeRuntimeKind } from '../stores/desktop-store';
import { useWindowStore } from '../stores/window-store';
import type { DesktopEntry } from '../types/desktop-entry';

/**
 * Resolve the canonical app identifier for a desktop entry.
 */
export function getAppId(entry: DesktopEntry): string {
	return entry.component || entry.exec || entry.id;
}

const DEFAULT_WINDOW_SIZE = { width: 800, height: 600 };

function resolveLaunchWindowGeometry(entry: DesktopEntry): { width: number; height: number; x?: number; y?: number } {
	const preferredWidth = entry.defaultWidth ?? DEFAULT_WINDOW_SIZE.width;
	const preferredHeight = entry.defaultHeight ?? DEFAULT_WINDOW_SIZE.height;
	if (typeof window === 'undefined') return { width: preferredWidth, height: preferredHeight };

	const viewportWidth = Math.max(window.innerWidth, DEFAULT_WINDOW_SIZE.width);
	const viewportHeight = Math.max(window.innerHeight, DEFAULT_WINDOW_SIZE.height);
	const marginX = entry.defaultWidth ? 48 : 0;
	const marginTop = entry.defaultHeight ? 64 : 0;
	const marginBottom = entry.defaultHeight ? 48 : 0;

	return {
		width: Math.max(DEFAULT_WINDOW_SIZE.width, Math.min(preferredWidth, viewportWidth - marginX)),
		height: Math.max(DEFAULT_WINDOW_SIZE.height, Math.min(preferredHeight, viewportHeight - marginTop - marginBottom)),
		...(entry.defaultWidth ? { x: Math.max(24, Math.floor(marginX / 2)) } : {}),
		...(entry.defaultHeight ? { y: Math.max(48, marginTop) } : {}),
	};
}

/**
 * Check whether the given permissions list allows opening an app.
 *
 * Accepts platform-issued forms:
 *  1. Owner wildcard — "*" grants every app.
 *  2. Bare app id — legacy form ("files", "terminal", "marketplace").
 *  3. Scoped wildcard — "app:*:open" / "app:*:*" grants every app.
 *  4. Scoped per-app — "app:<id>:open" / "app:<id>:*" / "app:<id>:client" / "app:<id>:admin".
 *
 * Empty permissions are not a launcher grant; bundled/core entries are handled
 * by `hasDesktopEntryPermission` below.
 * `app:*:client` and `app:*:admin` are intentionally NOT global launcher grants.
 */
export function hasAppPermission(appId: string, permissions: string[]): boolean {
	if (permissions.includes('*')) return true;
	if (permissions.includes(appId)) return true;
	if (permissions.includes('app:*:open') || permissions.includes('app:*:*')) return true;
	if (
		permissions.includes(`app:${appId}:open`) ||
		permissions.includes(`app:${appId}:*`) ||
		permissions.includes(`app:${appId}:client`) ||
		permissions.includes(`app:${appId}:admin`)
	) {
		return true;
	}
	return false;
}

function getPermissionIds(entry: DesktopEntry): string[] {
	return Array.from(new Set([getAppId(entry), entry.id, ...(entry.permissionAliases ?? [])].filter(Boolean)));
}

export function hasDesktopEntryPermission(entry: DesktopEntry, permissions: string[]): boolean {
	if (entry.type === 'builtin') return true;
	return getPermissionIds(entry).some((appId) => hasAppPermission(appId, permissions));
}

/**
 * Hook that returns a stable callback to launch any desktop entry.
 * Checks user permissions before opening; shows a notification if blocked.
 */
export function useLaunchApp() {
	const openWindow = useWindowStore((s) => s.openWindow);
	const auth = useKhalAuth();
	const permissions = auth?.permissions ?? [];

	const launch = useCallback(
		(entry: DesktopEntry) => {
			const appId = getAppId(entry);
			const geometry = resolveLaunchWindowGeometry(entry);

			// Permission gate: block if user lacks required permission
			if (!hasDesktopEntryPermission(entry, permissions)) {
				useNotificationStore.getState().addNotification({
					id: Date.now(),
					replacesId: 0,
					summary: 'Permission Denied',
					body: `You do not have permission to open ${entry.name}.`,
					icon: null,
					actions: [],
					expires: 4000,
					urgency: 'normal',
				});
				return;
			}

			const runtimeKind = normalizeRuntimeKind(entry.runtimeKind);
			const launchUrl = normalizeLaunchUrl(entry.launchUrl);
			openWindow({
				title: entry.name,
				appId,
				width: geometry.width,
				height: geometry.height,
				x: geometry.x,
				y: geometry.y,
				meta: {
					...(runtimeKind ? { runtimeKind } : {}),
					...(launchUrl ? { launchUrl } : {}),
					...(entry.launchToken ? { launchToken: entry.launchToken } : {}),
				},
			});

			// Auto-track app launch (fire-and-forget, non-blocking)
			try {
				const client = getNatsClient();
				client.publish(
					'os.apps.run.start',
					JSON.stringify({
						appId: appId,
						trigger: 'manual',
						userId: auth?.userId ?? 'anonymous',
					})
				);
			} catch {
				// Non-blocking -- tracking failure should not affect app launch
			}
		},
		[openWindow, permissions, auth]
	);

	return launch;
}
